import React, { useContext, useState, useEffect } from 'react';
import useQueryString from '../../../../hooks/use-query-string';
import MultiPoseBuilder from '../../_ElementaryMultiPose/MultiPosePackageBuilder/steps/MultiPoseBuilder';
import useBackgroundsFromPackage from '../../package-builder/use-backgrounds-from-package';
import useOption from '../../package-builder/use-option';
import usePackage from '../../package-builder/use-package';
import useImageTypesFromOption from '../../package-builder/use-image-types-from-option';
import { CustomerContext } from '../../../../contexts/CustomerContext';
import { usePoses } from '../../_ElementaryMultiPose/MultiPosePackageBuilder/hooks';
import { useHistory, useParams } from 'react-router-dom';
import Sheet from '../../../../models/Sheet';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { OrderPackage } from '../../../../models';
import { toast } from 'react-toastify';
import routes from '../../../../routes';
import { useStudent } from '../Yearbooks';

export function PackageLandingContent({ optionId, packageId, nextRoute = routes.review(), pushToNextRoute = true, afterAdd = () => {}, studentId }) {
  const backgrounds = useBackgroundsFromPackage(packageId);
  const option = useOption(optionId);
  const pricelist = usePackage(packageId);
  const { customer, setCustomer } = useContext(CustomerContext);
  const { data: poseData, fetching } = usePoses(studentId, [packageId]);
  const poses = poseData[packageId] || [];
  const imageTypes = useImageTypesFromOption(optionId, poses.length, false);
  const history = useHistory();

  useEffect(() => localStorage.setItem('isUsingAccessCode', true), []);

  const [sheets, setSheets] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(false);
  const [isCreated, setIsCreated] = useState(false);
  const [orderPackage, setOrderPackage] = useState(new OrderPackage());

  useEffect(() => {
    if (!optionId) return;
    if (!option) return;
    if (!option.digitalImagesOnly) return;
    if (!imageTypes || imageTypes.length < 1) return;

    const sheetsFromFavorites = poses.map((favorite, i) => {
      return new Sheet({ poseId: favorite.id, pose: favorite, imageType: imageTypes[0], imageTypeId: imageTypes[0].id, index: i });
    });

    setSheets(() => sheetsFromFavorites);
  }, [option && option.digitalImagesOnly, poses.length, imageTypes && imageTypes.length]);

  useDeepCompareEffect(() => {
    if (sheets.length === 0) return;
    if (!sheets.map((sheet) => sheet.poseId && sheet.backgroundId).reduce((acc, cur) => acc && cur, true)) return;

    setIsComplete(true);
  }, [sheets.map((sheet) => sheet.poseId && sheet.backgroundId)]);

  useDeepCompareEffect(() => {
    setIsCreated(false);
  }, [sheets.map((sheet) => `${sheet.poseId} - ${sheet.backgroundId}`)]);

  useEffect(() => {
    if (!isCreated) return;

    setTimeout(() => {
      setIsComplete(false);
      setIsCreated(false);
    }, 2000);
  }, [isCreated]);

  useEffect(() => {
    if (!isComplete) return;

    const saveOrderPackage = () => {
      const potentialExtraPoses = [...new Set(sheets.map((sheet) => sheet.poseId))].length
      const extraPoses = option.digitalImagesOnly ? 0 : Math.max(0, potentialExtraPoses - option.poses)

      orderPackage.cartId = customer.cart.id;
      orderPackage.studentId = studentId;
      orderPackage.optionId = option.id;
      orderPackage.packageId = packageId;
      orderPackage.apiOption = option;
      orderPackage.apiOption = option;
      orderPackage.extraPoses = extraPoses;
      orderPackage.sheets = sheets;

      orderPackage
        .save({ with: 'sheets' })
        .then(() => {
          setCustomer({ ...customer, currentOrderPackage: orderPackage });
          if (pushToNextRoute) history.push(nextRoute);
          afterAdd();
          setOrderPackage(orderPackage);
        })
        .catch(() => {
          setError(true);
          toast('There was an error adding this item to your cart. Please refresh and try again.', { type: 'error' })
        })
        .finally(() => setIsCreated(true));
    };

    saveOrderPackage();
  }, [isComplete]);

  if (!pricelist) return null;
  if (!option) return null;
  if (poses.length === 0) return null;
  if (!backgrounds) return null;

  return (
    <>
      {isComplete && isCreated && (
        <div style={{ textAlign: 'center', fontSize: '2em', fontWeight: 'bold' }}>Your package has been updated!</div>
      )}
      {isComplete && !isCreated && (
        <div style={{ textAlign: 'center', fontSize: '2em', fontWeight: 'bold' }}>Creating your package...</div>
      )}
      {!isComplete && (
        <h3 style={{ textAlign: 'center', transform: 'rotate(-3deg)', fontWeight: 'bold', margin: '2em 0', fontSize: '1.15rem' }}>
          Don&apos;t forget you can purchase all your images as digital!
        </h3>
      )}
      <MultiPoseBuilder
        placeholder={pricelist.placeholderImage()}
        checkBackgrounds
        images={poses}
        backgrounds={backgrounds}
        option={option}
        selectedPackage={pricelist}
        imageTypes={imageTypes}
        prefilledSheets={sheets}
        onComplete={(newSheets) => {
          setSheets(newSheets);
        }}
        checkForeground={poses.length > 0}
      />
    </>
  );
}

export default function PackageLanding() {
  const { o: optionId, sch: schoolId, sid: studentId } = useQueryString();
  const { student } = useStudent({ studentId, schoolId });
  const { packageId } = useParams();

  if (!student) return null;

  return <PackageLandingContent optionId={optionId} packageId={packageId} studentId={student.id} />;
}
